/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { chatService } from '@/api/services/chatService'
import { API_BASE_URL } from '@/shared/constants'
import { useAuthStore } from '@/store/useAuthStore'

// `pollMs` adds a polling fallback so updates still land if the WebSocket drops
// or is blocked (the socket remains the primary, instant path).
export function useConversations(p?: Parameters<typeof chatService.listConversations>[0], pollMs?: number) {
  return useQuery({
    queryKey: ['chat', 'conversations', p],
    queryFn: () => chatService.listConversations(p),
    staleTime: 3_000,
    refetchInterval: pollMs,
    refetchIntervalInBackground: false,
  })
}

export function useMessages(conversationId?: number, pollMs?: number) {
  return useQuery({
    queryKey: ['chat', 'messages', conversationId],
    queryFn: () => chatService.getMessages(conversationId!),
    enabled: !!conversationId,
    staleTime: 1_500,
    refetchInterval: conversationId ? pollMs : undefined,
    refetchIntervalInBackground: false,
  })
}

export function useSendMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, message }: { id: number; message: string }) => chatService.sendMessage(id, message),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['chat', 'messages', v.id] })
      qc.invalidateQueries({ queryKey: ['chat', 'conversations'] })
    },
  })
}

export function useStartConversation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { subject?: string; message: string }) => chatService.startConversation(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chat', 'conversations'] }),
  })
}

export function useAdminStartConversation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { user_id: number; subject?: string; message: string }) => chatService.adminStart(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chat', 'conversations'] }),
  })
}

export function useAssignConversation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, assignedTo }: { id: number; assignedTo: number | null }) => chatService.assign(id, assignedTo),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['chat', 'conversations'] })
      qc.invalidateQueries({ queryKey: ['chat', 'messages', v.id] })
    },
  })
}

export function useSetConversationStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) => chatService.setStatus(id, status),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['chat', 'conversations'] })
      qc.invalidateQueries({ queryKey: ['chat', 'messages', v.id] })
    },
  })
}

export function useAgents(enabled = true) {
  return useQuery({ queryKey: ['chat', 'agents'], queryFn: () => chatService.listAgents(), enabled, staleTime: 300_000 })
}

export function useUserSearch(search: string, enabled = true) {
  return useQuery({
    queryKey: ['chat', 'user-search', search],
    queryFn: () => chatService.searchUsers(search),
    enabled,
    staleTime: 30_000,
  })
}

export function useMarkRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => chatService.markRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat', 'conversations'] })
      qc.invalidateQueries({ queryKey: ['chat', 'unread'] })
    },
  })
}

export function useUnreadCount() {
  return useQuery({ queryKey: ['chat', 'unread'], queryFn: () => chatService.unreadCount(), staleTime: 10_000, refetchInterval: 60_000 })
}

// Derive the WebSocket URL from the REST base: http(s)://host/api/ → ws(s)://host/api/ws/chat/live
function chatWsUrl(token: string) {
  const base = API_BASE_URL.replace(/\/+$/, '')          // strip trailing slash
  const ws = base.replace(/^http/, 'ws')                 // http→ws, https→wss
  return `${ws}/ws/chat/live?token=${encodeURIComponent(token)}`
}

export interface ChatSocketEvent {
  type: 'message' | 'read' | 'conversation'
  conversation_id: number
  owner_id?: number
  data?: any
}

/**
 * Opens a chat WebSocket for the current user and invokes onEvent for each
 * server push. Auto-reconnects with backoff. Sends a keepalive ping.
 */
export function useChatSocket(onEvent: (e: ChatSocketEvent) => void, enabled = true) {
  const token = useAuthStore(s => s.token) ?? (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null)
  const cbRef = useRef(onEvent)
  cbRef.current = onEvent

  useEffect(() => {
    if (!enabled || !token) return
    let ws: WebSocket | null = null
    let ping: ReturnType<typeof setInterval> | null = null
    let retry: ReturnType<typeof setTimeout> | null = null
    let closed = false
    let attempts = 0

    const connect = () => {
      if (closed) return
      ws = new WebSocket(chatWsUrl(token))
      ws.onopen = () => {
        attempts = 0
        ping = setInterval(() => { try { ws?.send(JSON.stringify({ type: 'ping' })) } catch { /* noop */ } }, 25_000)
      }
      ws.onmessage = (ev) => {
        try {
          const parsed = JSON.parse(ev.data)
          if (parsed?.type === 'pong') return
          cbRef.current(parsed as ChatSocketEvent)
        } catch { /* ignore malformed */ }
      }
      ws.onclose = () => {
        if (ping) clearInterval(ping)
        if (closed) return
        attempts += 1
        const delay = Math.min(1000 * 2 ** attempts, 15_000)
        retry = setTimeout(connect, delay)
      }
      ws.onerror = () => { try { ws?.close() } catch { /* noop */ } }
    }
    connect()

    return () => {
      closed = true
      if (ping) clearInterval(ping)
      if (retry) clearTimeout(retry)
      try { ws?.close() } catch { /* noop */ }
    }
  }, [token, enabled])
}
